import {memo, useEffect, useState} from 'react';
import type {CSSProperties, ReactNode} from 'react';
import {useInView} from '@/hooks/useInView';
import styles from './FadeInUp.module.scss';

interface FadeInUpProps {
    children: ReactNode;
    className?: string;
    style?: CSSProperties;
}

// Same observer options AnimatedComponent used.
const IN_VIEW_OPTIONS = {triggerOnce: true, threshold: 0.1, rootMargin: '50px'} as const;

/**
 * CSS stand-in for <AnimatedComponent variants={fadeInUp}>, so sections ship no framer-motion. Same lifecycle: fully
 * visible in the server HTML (SEO), hidden once hydrated, then fades up the first time it nears the viewport. The
 * per-item `delay` callers used to pass never took effect (framer lets the variant's own transition win), so there is none.
 */
const FadeInUp = ({children, className, style}: FadeInUpProps) => {
    const [ref, inView] = useInView(IN_VIEW_OPTIONS);
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
    }, []);

    let stateClass: string | undefined;
    if (mounted) stateClass = inView ? styles.shown : styles.hidden;

    return (
        <div ref={ref} className={[className, stateClass].filter(Boolean).join(' ') || undefined} style={style}>
            {children}
        </div>
    );
};

export default memo(FadeInUp);
